import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GlassCard from "../../components/GlassCard";
import { getTodayPlan } from "../api/planApi";

const TodayPlanCard = () => {
  const navigate = useNavigate();
  const { lang } = useParams();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadTodayPlan() {
      try {
        setLoading(true);
        setError("");
        const data = await getTodayPlan();
        setPlan(data);
      } catch (err) {
        if (err.message === "No active plan found for current user") {
          setPlan(null);
          setError("");
        } else {
          setError(err.message || "Failed to load today's plan");
        }
      } finally {
        setLoading(false);
      }
    }

    loadTodayPlan();
  }, []);

  const todaysSessions = useMemo(() => {
    const formatDuration = (minutes) => {
      if (minutes === null || minutes === undefined) return "-";

      const totalMinutes = Number(minutes);
      if (Number.isNaN(totalMinutes)) return "-";

      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;

      return `${hours}:${String(mins).padStart(2, "0")}:00`;
    };

    return (plan?.sessions || []).map((session) => ({
      id: session.id,
      name: session.name ?? `Session ${session.id}`,
      length: formatDuration(session.estimated_time),
      completed: Boolean(session.completed),
    }));
  }, [plan]);

  const activeSessions = todaysSessions.filter((session) => !session.completed);
  const completedSessions = todaysSessions.filter((session) => session.completed);

  const handleGoToSession = (sessionId) => {
    navigate(`/${lang}/platform/session/${sessionId}`);
  };

  return (
    <GlassCard
      style={{
        padding: "20px",
        borderRadius: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        minHeight: "280px",
        marginLeft: "50px",
        marginTop: "25px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <span
          style={{
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            opacity: 0.7,
          }}
        >
          Today
        </span>

        <h3
          style={{
            margin: 0,
            fontSize: "1.3rem",
            fontWeight: 700,
          }}
        >
          {activeSessions.length > 0 ? "Plan for today" : "No more sessions for today"}
        </h3>

        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            opacity: 0.75,
          }}
        >
          {activeSessions.length > 0
            ? "Complete the remaining sessions below."
            : "All today’s sessions are completed."}
        </p>
      </div>

      {loading && (
        <div style={{ opacity: 0.8 }}>
          Loading today’s sessions...
        </div>
      )}

      {error && (
        <div style={{ color: "#ff6b6b" }}>
          {error}
        </div>
      )}

      {!loading && !error && todaysSessions.length === 0 && (
        <div style={{ opacity: 0.8 }}>
          No sessions scheduled for today.
        </div>
      )}

      {!loading && !error && activeSessions.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {activeSessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => handleGoToSession(session.id)}
              style={{
                border: "none",
                background: "rgba(255,255,255,0.28)",
                borderRadius: "16px",
                padding: "14px 16px",
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {session.name}
                </span>
              </div>

              <span
                style={{
                  fontSize: "0.92rem",
                  opacity: 0.75,
                  whiteSpace: "nowrap",
                }}
              >
                {session.length}
              </span>
            </button>
          ))}
        </div>
      )}

      {!loading && !error && completedSessions.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: activeSessions.length > 0 ? "4px" : "0px",
          }}
        >
          <div
            style={{
              fontSize: "0.9rem",
              fontWeight: 600,
              opacity: 0.7,
            }}
          >
            Completed:
          </div>

          {completedSessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => handleGoToSession(session.id)}
              style={{
                border: "none",
                borderRadius: "16px",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                opacity: 0.75,
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "0.98rem",
                  textDecoration: "line-through",
                }}
              >
                {session.name}
              </span>

              <span
                style={{
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                }}
              >
                {session.length}
              </span>
            </button>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default TodayPlanCard;